import * as Sentry from "@sentry/browser";
import Cookies from "js-cookie";
import { getAccessToken as getAuthClientAccessToken } from "./auth-client";

// Only apply the configured domain to cookies if the current hostname is that
// domain or a subdomain of it. Otherwise (local dev, Codespaces, preview URLs)
// fall back to null so the browser scopes cookies to the current host — a
// mismatched or empty `domain` attribute causes the browser to silently drop
// the cookie or store it host-only, leading to duplicate cookies across scopes
// when another build/repo writes the same names with a correct domain.
const configuredDomain = process.env.REACT_APP_DOMAIN;
const hostMatchesConfiguredDomain = () => {
  if (!configuredDomain || typeof window === "undefined") {
    return false;
  }
  const host = window.location.hostname;
  const target = configuredDomain.startsWith(".")
    ? configuredDomain.slice(1)
    : configuredDomain;
  return host === target || host.endsWith(`.${target}`);
};
const domain = hostMatchesConfiguredDomain() ? configuredDomain : null;

export const clearAuthCookies = () => {
  const cookieNames = [
    "access_token_expiration",
    "access_token",
    "google_api_access_token_expiration",
    "google_api_access_token",
    "login_method",
  ];

  // Build the set of domains to clear. Cookies may have been written under different
  // domain scopes (e.g. .tinuiti.com vs .blisspoint.tinuiti.com), so we must
  // remove all variants to avoid stale cookies causing auth loops on logout/sign-in.
  const { hostname } = window.location;
  const parts = hostname.split(".");
  const domainsToRemove = new Set([domain, hostname, `.${hostname}`]);

  // Also target the parent domain (e.g. "tinuiti.com" / ".tinuiti.com")
  if (parts.length >= 3) {
    const parentDomain = parts.slice(-2).join(".");
    domainsToRemove.add(parentDomain);
    domainsToRemove.add(`.${parentDomain}`);
  }

  for (const d of domainsToRemove) {
    for (const name of cookieNames) {
      Cookies.remove(name, { domain: d });
    }
  }
  // Also remove without a domain attribute to catch any cookies scoped to the exact origin
  for (const name of cookieNames) {
    Cookies.remove(name);
  }
};

const ALL_AUTH_COOKIE_NAMES = [
  "access_token",
  "access_token_expiration",
  "google_api_access_token",
  "google_api_access_token_expiration",
  "login_method",
  "google_refresh_token",
];

// Detects stale duplicate auth cookies (e.g. a host-only entry from a
// misconfigured build sitting alongside a correctly domain-scoped entry from a
// later build). When the same auth cookie name appears more than once in
// document.cookie, js-cookie reads the older copy per RFC 6265 §5.4 — which
// is almost always wrong and produces self-perpetuating auth loops. The only
// safe recovery is to clear all auth state and force re-login. Call this once
// at app startup, before any auth read.
export const detectAndPurgeDuplicateAuthCookies = () => {
  if (typeof document === "undefined" || !document.cookie) {
    return;
  }
  const counts = {};
  for (const segment of document.cookie.split(/;\s*/)) {
    const name = segment.split("=")[0];
    if (ALL_AUTH_COOKIE_NAMES.includes(name)) {
      counts[name] = (counts[name] || 0) + 1;
    }
  }
  const duplicates = Object.keys(counts).filter(name => counts[name] > 1);
  if (duplicates.length === 0) {
    return;
  }
  Sentry.captureMessage(
    `Duplicate auth cookies detected — purging (${duplicates.join(", ")})`,
    "warning"
  );
  clearAuthCookies();
  // clearAuthCookies doesn't touch google_refresh_token; remove it across the
  // same scope set so a stale duplicate doesn't survive the purge.
  const { hostname } = window.location;
  const parts = hostname.split(".");
  const domainsToRemove = new Set([domain, hostname, `.${hostname}`]);
  if (parts.length >= 3) {
    const parentDomain = parts.slice(-2).join(".");
    domainsToRemove.add(parentDomain);
    domainsToRemove.add(`.${parentDomain}`);
  }
  for (const d of domainsToRemove) {
    Cookies.remove("google_refresh_token", { domain: d });
  }
  Cookies.remove("google_refresh_token");
  document.location.reload();
};

// Use this function (not Cookies.get("access_token")) to obtain the bearer token for API calls.
export const getAccessToken = opts => getAuthClientAccessToken(opts);
